import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { toast } from 'react-toastify';

import {
  FBoxContainer,
  FHTextarea,
  FHButton,
  FHConfirmDialog,
  FHTimer,
  FHWaveLoader,
} from '../../../../../components/base';
import {
  FCheckbox,
  FQuillEditor,
  FTimePicker,
  getQuillPlainText,
  sanitizeQuillHtml,
} from '../../../../../components/baseV2';
import { sessionActions } from '../../../../../store';
import { getUtcOffset } from '../../../../../utils/common';
import { BTSessionStatus, Themes } from '../../../../../constants';
import { NARRATIVE_AI_ALLOWED_FAILURE_COUNT } from '../../constants';
import { FORTA_BLUE, FORTA_ERROR, FORTA_GRAY4, FORTA_WHITE } from '../../../../../utils/colors';
import LogoIcon from '../../../../../components/svgs/LogoIcon';
import ErrorAlertIcon from '../../../../../components/svgs/ErrorAlertIcon';
import EditOutlinedIcon from '../../../../../components/svgs/EditOutlinedIcon';
import TrashIcon from '../../../../../components/svgs/TrashIcon3';
import { useTranslation } from 'react-i18next';
import {
  ADD_BREAK_BUTTON_SX,
  ADD_BREAK_BUTTON_WRAPPER_SX,
  SESSION_BREAK_ACTIONS_SX,
  SESSION_BREAK_CARD_SX,
  SESSION_BREAK_CHECKBOX_LABEL_SX,
  SESSION_BREAK_FIELDS_SX,
  SESSION_BREAK_ICON_BUTTON_SX,
  SESSION_BREAK_LIST_SX,
  SESSION_BREAK_ROW_SX,
} from '../../styles';
import {
  getSessionBreakDateTime,
  getSessionBreakUnits,
  getSessionBreakValidationMessage,
  getSessionOffsetSeconds,
} from '../../utils';

import type { RootState } from '../../../../../store';
import type { ChangeEvent } from 'react';
import type { Moment } from 'moment';
import type { IBTSession, ISessionBreak } from '../../../../../utils/types';

function getSuggestedNarrativeHtml(narrative: string): string {
  if (!narrative) return '';

  const container = document.createElement('div');
  container.textContent = narrative;

  return `<p>${container.innerHTML}</p>`;
}

interface ISessionNarrativeProps {
  isEditable: boolean;
  session: IBTSession | null;
  currentNarrative: string;
  minimumLength: number;
  onChange: (key: string, value: any) => void;
  showError?: boolean;
  useNewUi?: boolean;
}

function SessionNarrative({
  isEditable,
  session,
  currentNarrative,
  minimumLength = 180,
  onChange,
  showError,
  useNewUi = false,
}: ISessionNarrativeProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation('sessionNotes');
  const { getAccessTokenSilently } = useAuth0();

  const aiSuggestionAllowed = isEditable && session?.status === BTSessionStatus.Started;

  const { gptNarrativeFlag, narrativeSuggestionLoading } = useSelector(
    (state: RootState) => state.sessions,
  );

  const [isError, setIsError] = useState<boolean | null>(null);
  const [generatingSuggestions, setGeneratingSuggestions] = useState<boolean>(false);
  const [isAcceptingAISuggestion, setIsAcceptingAISuggestion] = useState<boolean>(false);
  const [unsavedAISuggestion, setUnsavedAISuggestion] = useState<string>('');
  // The saved narrative at the failed attempt. Retry is offered only while it is unchanged.
  const [unsavedAISuggestionBase, setUnsavedAISuggestionBase] = useState<string>('');
  const [disclaimer, setDisclaimer] = useState<string>(
    'Forta AI suggested the following session narrative. You must review and edit the content to ensure accuracy and completeness. ',
  );
  const [disableGenerate, setDisableGenerate] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [failureCount, setFailureCount] = useState<number>(0);
  const [breaksTaken, setBreaksTaken] = useState<boolean>(false);
  const [breakStartValue, setBreakStartValue] = useState<Moment | null>(null);
  const [breakEndValue, setBreakEndValue] = useState<Moment | null>(null);
  const [sessionBreaks, setSessionBreaks] = useState<ISessionBreak[]>([]);
  const [selectedBreakIndex, setSelectedBreakIndex] = useState<number | null>(null);
  const narrativeRequestContextTokenRef = useRef(0);
  const narrativeSuggestionRequestIdRef = useRef(0);
  // State updates land on the next render, so a ref blocks a second request from a quick double click.
  const narrativeGenerationLockedRef = useRef(false);
  const activeSessionIdRef = useRef(session?.id);
  const isMountedRef = useRef(false);

  activeSessionIdRef.current = session?.id;

  const isCurrentNarrativeRequest = (sessionId: string, requestContextToken: number) =>
    isMountedRef.current &&
    activeSessionIdRef.current === sessionId &&
    narrativeRequestContextTokenRef.current === requestContextToken;

  const narrativeContextToken = `${session?.id ?? ''}:${gptNarrativeFlag}`;

  useLayoutEffect(() => {
    if (!session?.id) return;
    dispatch(
      sessionActions.setNarrativeContext({
        sessionId: session.id,
        contextToken: narrativeContextToken,
      }),
    );
  }, [dispatch, narrativeContextToken, session?.id]);

  const sessionStartDate = session?.date_start ? moment(session.date_start) : null;
  const sessionEndDate =
    sessionStartDate && session?.expected_duration
      ? sessionStartDate.clone().add(session.expected_duration, 'seconds')
      : null;
  const getComparableNarrative = (narrative?: string | null) =>
    useNewUi ? getQuillPlainText(narrative) : narrative?.trim() ?? '';

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      narrativeRequestContextTokenRef.current += 1;
    };
  }, []);

  useEffect(() => {
    const requestContextToken = ++narrativeRequestContextTokenRef.current;
    narrativeSuggestionRequestIdRef.current += 1;
    narrativeGenerationLockedRef.current = false;
    setGeneratingSuggestions(false);

    return () => {
      if (narrativeRequestContextTokenRef.current === requestContextToken) {
        narrativeRequestContextTokenRef.current += 1;
      }
    };
  }, [gptNarrativeFlag, session?.id]);

  useEffect(() => {
    const nextBreaks = session?.breaks ?? [];
    setSessionBreaks(nextBreaks);
    setBreaksTaken(nextBreaks.length > 0);
    setBreakStartValue(null);
    setBreakEndValue(null);
    setSelectedBreakIndex(null);
  }, [session?.id, session?.breaks]);

  useEffect(() => {
    if (!minimumLength) return;
    const currentNarrativeText = getComparableNarrative(currentNarrative);
    if (!currentNarrativeText) {
      if (showError) {
        setIsError(true);
      } else {
        return;
      }
    }
    if (currentNarrativeText.length < minimumLength) {
      setIsError(true);
    } else {
      setIsError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimumLength, currentNarrative, showError, useNewUi]);

  useEffect(() => {
    if (!session?.id || !aiSuggestionAllowed) return;
    // Fill an empty narrative only. A dialog opens only when the clinician selects Suggest.
    if (getComparableNarrative(currentNarrative)) return;
    handleGenerateNarrative();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSuggestionAllowed, gptNarrativeFlag, session?.id]);

  useEffect(() => {
    setIsAcceptingAISuggestion(false);
    setUnsavedAISuggestion('');
  }, [gptNarrativeFlag, session?.id]);

  useEffect(() => {
    if (!useNewUi || !disableGenerate || failureCount >= NARRATIVE_AI_ALLOWED_FAILURE_COUNT) return;

    const retryTimer = window.setTimeout(() => {
      setDisableGenerate(false);
      setDisclaimer("Select 'Suggest Narrative' to prompt Forta AI to generate a session narrative");
    }, 60000);

    return () => window.clearTimeout(retryTimer);
  }, [disableGenerate, failureCount, useNewUi]);

  const handleGenerateNarrative = async () => {
    if (!session?.id || narrativeGenerationLockedRef.current) return;
    const requestedSessionId = session.id;
    const requestContextToken = narrativeRequestContextTokenRef.current;
    const suggestionRequestId = ++narrativeSuggestionRequestIdRef.current;
    narrativeGenerationLockedRef.current = true;
    setUnsavedAISuggestion('');
    setGeneratingSuggestions(true);
    const result = await dispatch(
      sessionActions.getSessionNarrativeSuggestion({
        getAccessTokenSilently,
        sessionId: requestedSessionId,
      }),
    );
    if (
      suggestionRequestId !== narrativeSuggestionRequestIdRef.current ||
      !isCurrentNarrativeRequest(requestedSessionId, requestContextToken)
    ) {
      return;
    }
    // If Forta AI fails to generate a result, handle error.
    if (result?.error) {
      narrativeGenerationLockedRef.current = false;
      setGeneratingSuggestions(false);
      setDisclaimer(
        failureCount >= 2
          ? 'Forta AI is currently unavailable. Please proceed by writing your narrative.'
          : 'Forta AI is having trouble generating a session narrative.',
      );
      setDisableGenerate(true);
      setFailureCount((prev) => prev + 1);
      return;
    }
    // The replace warning was shown before the request, so apply the text now.
    const newNarrative = result?.payload?.narrative?.trim() ?? '';
    setFailureCount(0);
    setDisableGenerate(false);
    setDisclaimer(
      'Forta AI suggested the following session narrative. You must review and edit the content to ensure accuracy and completeness.',
    );
    await saveSuggestedNarrative(newNarrative);
    if (
      suggestionRequestId !== narrativeSuggestionRequestIdRef.current ||
      !isCurrentNarrativeRequest(requestedSessionId, requestContextToken)
    ) {
      return;
    }
    narrativeGenerationLockedRef.current = false;
    setGeneratingSuggestions(false);
  };

  const saveSuggestedNarrative = async (newNarrative: string) => {
    if (!newNarrative || isAcceptingAISuggestion) return;
    const narrativeValue = useNewUi ? getSuggestedNarrativeHtml(newNarrative) : newNarrative;
    const requestedSessionId = session?.id ?? '';
    const requestContextToken = narrativeRequestContextTokenRef.current;
    setIsAcceptingAISuggestion(true);
    const result = await saveNarrative(narrativeValue, narrativeValue, narrativeContextToken);
    setIsAcceptingAISuggestion(false);
    if (!isCurrentNarrativeRequest(requestedSessionId, requestContextToken)) return;
    if (!result || result.error) {
      if (result?.error?.code === '409' && requestedSessionId) {
        // The note changed on the server since this suggestion was generated. Refetch the
        // note so the stale cached suggestion is replaced instead of silently reapplied.
        dispatch(
          sessionActions.getSession({ sessionId: requestedSessionId, getAccessTokenSilently }),
        );
      } else {
        // Keep the suggestion so the clinician can save it again without a new request.
        setUnsavedAISuggestion(newNarrative);
        setUnsavedAISuggestionBase(session?.narrative ?? '');
      }
      return;
    }
    setUnsavedAISuggestion('');

    onChange('narrative', narrativeValue);
    dispatch(
      sessionActions.updateState({
        key: 'current',
        data: {
          ...session,
          narrative: narrativeValue,
          breaks: session?.breaks ?? sessionBreaks,
          last_ai_generated_session_narrative: narrativeValue,
        },
      }),
    );
  };

  const saveNarrative = (
    newNarrative: string,
    aiNarrative?: string,
    contextToken?: string,
  ) => {
    if (!session) return Promise.resolve({ error: true });
    const data = {
      narrative: newNarrative,
      ...(aiNarrative !== undefined && {
        last_ai_generated_session_narrative: aiNarrative,
      }),
    };
    return dispatch(
      sessionActions.updateSessionNarrative({
        sessionId: session.id,
        data,
        narrativeContextToken: contextToken,
        getAccessTokenSilently,
      }),
    );
  };

  const onGenerate = () => {
    if (generatingSuggestions || isAcceptingAISuggestion) return;
    if (getComparableNarrative(currentNarrative)) {
      setOpenConfirm(true);
    } else {
      handleGenerateNarrative();
    }
  };

  const handlePersistSessionBreaks = async (nextBreaks: ISessionBreak[]) => {
    if (!session?.id) return false;

    const result = await dispatch(
      sessionActions.updateSessionHeaderInfo({
        sessionId: session.id,
        data: {
          utc_offset: getUtcOffset(moment(new Date())),
          breaks: nextBreaks.map(({ start_offset_seconds, end_offset_seconds }) => ({
            start_offset_seconds,
            end_offset_seconds,
          })),
        },
        updateGPTNarrativeFlag: true,
        getAccessTokenSilently,
      }),
    );

    if (!result.payload || result.error) {
      toast.error('Break time could not be saved');
      return false;
    }

    // SAFETY: A fulfilled session-header update returns a session-shaped payload.
    const updatedSession = result.payload as Partial<IBTSession>;
    const updatedBreaks = updatedSession.breaks ?? nextBreaks;
    setSessionBreaks(updatedBreaks);
    setBreaksTaken(updatedBreaks.length > 0);
    return true;
  };

  const clearBreakForm = () => {
    setBreakStartValue(null);
    setBreakEndValue(null);
    setSelectedBreakIndex(null);
  };

  const handleToggleBreaksTaken = async (checked: boolean) => {
    setBreaksTaken(checked);
    if (checked) return;

    const cleared = await handlePersistSessionBreaks([]);
    if (cleared) {
      clearBreakForm();
      return;
    }
    setBreaksTaken(true);
  };

  const handleSaveBreak = async () => {
    if (!sessionStartDate || !sessionEndDate || !breakStartValue || !breakEndValue) return;

    const breakStart = getSessionBreakDateTime(sessionStartDate, sessionEndDate, breakStartValue);
    const breakEnd = getSessionBreakDateTime(sessionStartDate, sessionEndDate, breakEndValue);
    const validationMessage = getSessionBreakValidationMessage(
      sessionStartDate,
      sessionEndDate,
      breakStart,
      breakEnd,
    );

    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    const nextBreak: ISessionBreak = {
      start_offset_seconds: getSessionOffsetSeconds(sessionStartDate, breakStart),
      end_offset_seconds: getSessionOffsetSeconds(sessionStartDate, breakEnd),
    };
    const nextBreaks =
      selectedBreakIndex === null
        ? [...sessionBreaks, nextBreak]
        : sessionBreaks.map((sessionBreak, index) =>
            index === selectedBreakIndex ? nextBreak : sessionBreak,
          );

    const saved = await handlePersistSessionBreaks(nextBreaks);
    if (saved) {
      clearBreakForm();
    }
  };

  const handleEditBreak = (index: number) => {
    const sessionBreak = sessionBreaks[index];
    if (!sessionStartDate || !sessionBreak) return;

    setBreaksTaken(true);
    setSelectedBreakIndex(index);
    setBreakStartValue(sessionStartDate.clone().add(sessionBreak.start_offset_seconds, 'seconds'));
    setBreakEndValue(sessionStartDate.clone().add(sessionBreak.end_offset_seconds, 'seconds'));
  };

  const handleDeleteBreak = async (index: number) => {
    const nextBreaks = sessionBreaks.filter((_, currentIndex) => currentIndex !== index);
    const saved = await handlePersistSessionBreaks(nextBreaks);
    if (saved && selectedBreakIndex === index) {
      clearBreakForm();
    }
  };

  const breakEditor =
    useNewUi && session ? (
      <Box sx={SESSION_BREAK_CARD_SX}>
        <FCheckbox
          label="Breaks were taken during session, where no goal were practiced"
          checked={breaksTaken}
          disabled={!isEditable || narrativeSuggestionLoading}
          onChange={(event) => handleToggleBreaksTaken(event.target.checked)}
          formControlLabelProps={{ sx: SESSION_BREAK_CHECKBOX_LABEL_SX }}
        />
        {breaksTaken && (
          <>
            <Box sx={SESSION_BREAK_FIELDS_SX}>
              <FTimePicker
                label="Break start"
                required
                fullWidth
                format="h:mm A"
                showClockIcon
                use12Hours
                allowClear
                placeholder="Select start"
                disabled={!isEditable || narrativeSuggestionLoading}
                value={breakStartValue}
                onChange={setBreakStartValue}
              />
              <FTimePicker
                label="Break end"
                required
                fullWidth
                format="h:mm A"
                showClockIcon
                use12Hours
                allowClear
                placeholder="Select end"
                disabled={!isEditable || narrativeSuggestionLoading}
                value={breakEndValue}
                onChange={setBreakEndValue}
              />
            </Box>
            <Box sx={ADD_BREAK_BUTTON_WRAPPER_SX}>
              <FHButton
                sx={ADD_BREAK_BUTTON_SX}
                disabled={!isEditable || narrativeSuggestionLoading || !breakStartValue || !breakEndValue}
                onClick={handleSaveBreak}
              >
                {selectedBreakIndex === null ? 'Add Break' : 'Save'}
              </FHButton>
            </Box>
            {sessionBreaks.length > 0 && (
              <Box sx={SESSION_BREAK_LIST_SX}>
                {sessionBreaks.map((sessionBreak, index) => {
                  const breakStart = sessionStartDate
                    ? sessionStartDate.clone().add(sessionBreak.start_offset_seconds, 'seconds')
                    : null;
                  const breakEnd = sessionStartDate
                    ? sessionStartDate.clone().add(sessionBreak.end_offset_seconds, 'seconds')
                    : null;
                  const breakDurationSeconds =
                    sessionBreak.end_offset_seconds - sessionBreak.start_offset_seconds;

                  return (
                    <Box key={`${sessionBreak.start_offset_seconds}-${index}`} sx={SESSION_BREAK_ROW_SX}>
                      <Typography>
                        Break {index + 1} - {breakStart?.format('h:mm A')} -{' '}
                        {breakEnd?.format('h:mm A')} ({getSessionBreakUnits(breakDurationSeconds)}{' '}
                        unit)
                      </Typography>
                      <Box sx={SESSION_BREAK_ACTIONS_SX}>
                        <IconButton
                          sx={SESSION_BREAK_ICON_BUTTON_SX}
                          disabled={!isEditable || narrativeSuggestionLoading}
                          onClick={() => handleEditBreak(index)}
                          aria-label={`Edit break ${index + 1}`}
                        >
                          <EditOutlinedIcon color={FORTA_BLUE.primary} width={20} height={20} />
                        </IconButton>
                        <IconButton
                          sx={SESSION_BREAK_ICON_BUTTON_SX}
                          disabled={!isEditable || narrativeSuggestionLoading}
                          onClick={() => handleDeleteBreak(index)}
                          aria-label={`Delete break ${index + 1}`}
                        >
                          <TrashIcon color={FORTA_BLUE.primary} width={18} height={18} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Box>
    ) : null;

  const handleNarrativeChange = (newNarrative: string) => {
    setUnsavedAISuggestion('');
    onChange('narrative', newNarrative);
    setIsError(getComparableNarrative(newNarrative).length < minimumLength);
  };

  const handleNarrativeBlur = (newNarrative: string) => {
    const comparableNarrative = getComparableNarrative(newNarrative);
    onChange('narrative', newNarrative);
    if (comparableNarrative !== getComparableNarrative(session?.narrative)) {
      saveNarrative(newNarrative);
    }
  };

  return isEditable ? (
    <>
      {breakEditor}
      <FBoxContainer
        sx={{
          my: 3,
          flexGrow: 0,
          borderRadius: '10px',
          boxShadow: 'none',
          ...(useNewUi && {
            backgroundColor: 'transparent',
            border: 'none',
          }),
        }}
        contentStyle={useNewUi ? { p: 0 } : undefined}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography fontSize={21} align="left" mb={1} color={FORTA_BLUE.primary}>
            {t('sessionNarrative')}
          </Typography>
          {disclaimer && aiSuggestionAllowed && (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1, mb: 1 }}>
              <ErrorAlertIcon color={FORTA_ERROR} />
              <Typography textAlign="left" color={FORTA_ERROR} ml={1}>
                {disclaimer}
              </Typography>
            </Box>
          )}
          <Box sx={{ flexGrow: 1, position: 'relative', mt: 2 }}>
            {useNewUi ? (
              <FQuillEditor
                value={currentNarrative ?? ''}
                label={t('sessionNarrative')}
                required
                minHeight={120}
                placeholder="Describe the session and client's response"
                disabled={
                  !isEditable ||
                  narrativeSuggestionLoading ||
                  generatingSuggestions ||
                  isAcceptingAISuggestion
                }
                onToolbarSuggest={aiSuggestionAllowed ? onGenerate : undefined}
                toolbarSuggestDisabled={
                  disableGenerate ||
                  failureCount >= NARRATIVE_AI_ALLOWED_FAILURE_COUNT ||
                  generatingSuggestions ||
                  isAcceptingAISuggestion
                }
                toolbarSuggestLabel="Suggest narrative"
                onChange={handleNarrativeChange}
                onBlur={handleNarrativeBlur}
                error={isEditable && isError === true}
                helperText={isEditable ? `Minimum ${minimumLength} characters` : ''}
              />
            ) : (
              <FHTextarea
                data-testid="descriptionSessionNarrative"
                colorTheme={Themes.Primary}
                minRows={8}
                maxRows={40}
                placeholder="Describe the session and client's response"
                disabled={
                  !isEditable ||
                  narrativeSuggestionLoading ||
                  generatingSuggestions ||
                  isAcceptingAISuggestion
                }
                expandable={false}
                value={currentNarrative ?? ''}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  handleNarrativeChange(e?.target.value);
                }}
                onBlur={(e) => {
                  const nextNarrative = e?.target.value?.trim();
                  onChange('narrative', nextNarrative);
                  if (nextNarrative !== session?.narrative?.trim()) {
                    saveNarrative(nextNarrative);
                  }
                }}
                error={isEditable && isError === true}
                helperText={isEditable ? `Minimum ${minimumLength} characters` : ''}
              />
            )}
            {generatingSuggestions && (
              <Box
                sx={{
                  backgroundColor: FORTA_WHITE,
                  position: 'absolute',
                  top: 0,
                  width: '100%',
                  height: '90%',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FHWaveLoader text={'Forta AI is generating your session narrative'} />
              </Box>
            )}
          </Box>
        </Box>
        {unsavedAISuggestion &&
          aiSuggestionAllowed &&
          (session?.narrative ?? '') === unsavedAISuggestionBase && (
          <Box
            data-testid="session-narrative-unsaved-suggestion"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}
          >
            <Typography color={FORTA_ERROR}>The suggested narrative was not saved.</Typography>
            <FHButton
              disabled={isAcceptingAISuggestion}
              onClick={() => void saveSuggestedNarrative(unsavedAISuggestion)}
            >
              {isAcceptingAISuggestion ? 'Saving...' : 'Retry save'}
            </FHButton>
          </Box>
        )}
        {aiSuggestionAllowed && !useNewUi && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <FHButton
              sx={{
                mt: 3,
                backgroundColor: FORTA_WHITE,
                borderWidth: 1,
                borderStyle: 'solid',
                color: FORTA_BLUE.primary,
                borderColor: FORTA_BLUE.light2,
                ':hover': { color: FORTA_BLUE.primary, backgroundColor: FORTA_WHITE },
                ':disabled': {
                  color: FORTA_BLUE.primary,
                  backgroundColor: FORTA_WHITE,
                  opacity: 0.7,
                  border: 'none',
                },
              }}
              disabled={
                disableGenerate ||
                failureCount >= NARRATIVE_AI_ALLOWED_FAILURE_COUNT ||
                generatingSuggestions ||
                isAcceptingAISuggestion
              }
              onClick={onGenerate}
              startIcon={<LogoIcon color={FORTA_BLUE.primary} />}
            >
              {disableGenerate && failureCount < NARRATIVE_AI_ALLOWED_FAILURE_COUNT ? (
                <Box sx={{ width: '130px', display: 'flex' }}>
                  <Typography mr={1}>Retry in: </Typography>
                  <FHTimer
                    duration={60}
                    onTimerComplete={() => {
                      setDisableGenerate(false);
                      setDisclaimer(
                        "Select 'Suggest Narrative' to prompt Forta AI to generate a session narrative",
                      );
                    }}
                  />
                </Box>
              ) : (
                'Suggest Narrative'
              )}
            </FHButton>
          </Box>
        )}

        <FHConfirmDialog
          open={openConfirm}
          title="Suggest Narrative with Forta AI"
          titleStyles={{ color: FORTA_BLUE.primary }}
          dialogStyles={{
            padding: '16px',
            maxHeight: '85vh',
            height: 'auto',
            width: '580px',
            maxWidth: '580px',
          }}
          sx={{
            '& .MuiModal-backdrop': {
              backdropFilter: 'blur(2px)',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
          contentRenderer={() => (
            <Box px={2} py={4}>
              <Typography display="inline">
                Generating a new narrative will erase the current session narrative you have already
                input. Do you wish to continue?
              </Typography>
            </Box>
          )}
          submittext="Continue"
          canceltext="Cancel"
          onClose={(_, reason) => {
            if (reason && reason === 'backdropClick') return;
            setOpenConfirm(false);
          }}
          onSubmit={() => {
            setOpenConfirm(false);
            handleGenerateNarrative();
          }}
        />

      </FBoxContainer>
    </>
  ) : (
    <>
      {breakEditor}
      <FBoxContainer
        sx={{
          my: 3,
          position: 'relative',
          borderRadius: '10px',
          boxShadow: 'none',
          ...(useNewUi && {
            backgroundColor: 'transparent',
            border: 'none',
          }),
        }}
        contentStyle={{
          pt: 2.5,
          ...(useNewUi && {
            p: 0,
          }),
        }}
      >
        <Typography fontSize={21} color={FORTA_BLUE.primary} textAlign="left">
          {t('sessionNarrative')}
        </Typography>
        {useNewUi ? (
          <Box
            sx={{
              color: FORTA_GRAY4,
              textAlign: 'left',
              mt: 3,
              '& p': { m: 0, mb: 1 },
              '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeQuillHtml(currentNarrative) }}
          />
        ) : (
          <Typography color={FORTA_GRAY4} textAlign={'left'} mt={3}>
            {currentNarrative}
          </Typography>
        )}
      </FBoxContainer>
    </>
  );
}

export default SessionNarrative;
